/* eslint-disable camelcase */
/*************************************************************************
 * ADOBE SYSTEMS INCORPORATED
 *  Copyright 2018 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this file in
 * accordance with the terms of the Adobe license agreement accompanying it.
 * If you have received this file from a source other than Adobe, then your
 * use, modification, or distribution of it requires the prior written
 * permission of Adobe.
 **************************************************************************/
/**
 * @file
 */
import { v4 as uuidv4 } from 'uuid';
import { http } from 'adobe-dcapi-web';
import template from 'url-template';
import { getEnvVar } from '../core/EnvUtil';

import { getSingletonFunction } from '../core/ProviderUtil';
import DcapiAPI from './DcapiAPI';
import { auth2 } from './Auth2API';
import { logging } from './LoggingAPI';
import globalContextManager from '../core/GlobalContextManager';
import { createMockAnonUser } from '../core/CommonUtil';
import MeterIQLimitsProvider from './MeterIQLimitsProvider';

export const logger = logging.getLogger('UserAPI');
export const CONTENT_TYPE_USER = 'user_v1.json';
export const CONTENT_TYPE_LIMITS_ESIGN = 'user_limits_esign_v1.json';
export const CONTENT_TYPE_USER_PREFS = 'user_prefs_v1.json';
export const CONTENT_TYPE_METERIQ_LIMITS = 'user_limits_verbs_v2.json';

const isMeterIQEnabled = (): boolean => window.adobe_dc_sdk?.launchOptions?.meteriq?.enable ?? false;

/**
 * Wait for auth state to be determined (either signed in or not)
 * Returns when auth2.isSignedIn is no longer undefined
 */
const waitForAuthState = (): Promise<void> => new Promise(resolve => {
  // If already determined, resolve immediately
  if (auth2.isSignedIn !== undefined) {
    resolve();
    return;
  }

  // Otherwise, listen for state changes
  const listener = state => {
    if (state.isSignedIn !== undefined) {
      auth2.unuseState(listener);
      resolve();
    }
  };

  auth2.useState(listener);
});

export const OPERATION_USER_GET = 'users.get_user';
export const OPERATION_LIMITS_ESIGN = 'users.get_limits_esign';
export const OPERATION_PREFS_GET = 'users.get_prefs';
export const OPERATION_PREFS_PUT = 'users.put_prefs';
export const OPERATION_GET_METERIQ_LIMITS = 'users.get_limits_verbs_v2';

const FREE = 'Free';
const TRIAL = 'Trial';
const BASIC = 'Basic';
const PLUS = 'Plus';
const PREMIUM_PLUS = 'Premium Plus';
const PREMIUM_BASIC = 'Premium Basic';

const CREATE_PDF = 'CreatePDF';
const PDF_PACK = 'PDFPack';
const EXPORT_PDF = 'ExportPDF';
const FILES = 'Files';
const DC_GLOBAL = 'DCGlobal';
const DC_ENTERPRISE = 'DCEnterprise';
const ACROBAT_PLUS = 'AcrobatPlus';
const ACROBAT_STD = 'AcrobatStd';
const E_SIGN = 'ESign';
const SEND_AND_TRACK = 'SendNow';
const EDIT_PDF = 'EditPDF';
const OCR_PDF = 'OcrPDF';
const DC_LITE = 'DCLite';
const SCAN_PLUS = 'ScanPlus';
const GENAI_SERVICES = 'GenAIServices';
const PDF_SERVICES = 'PDFServices';

const ADOBE_ACROBAT_STUDIO = 'ADOBE_ACROBAT_STUDIO';
const ACROBAT_STUDIO_FOR_TEAMS = 'ACROBAT_STUDIO_FOR_TEAMS';
const ACROBAT_STUDIO_FOR_EDUCATION_HED_STUDENT = 'ACROBAT_STUDIO_FOR_EDUCATION_HED_STUDENT';
const ACROBAT_STUDIO_FOR_ENTERPRISE = 'ACROBAT_STUDIO_FOR_ENTERPRISE';
const ACROBAT_STUDIO_FOR_EDUCATION_HED_FACULTY_STAFF = 'ACROBAT_STUDIO_FOR_EDUCATION_HED_FACULTY_STAFF';

const STUDIO_CODES = [
  ADOBE_ACROBAT_STUDIO,
  ACROBAT_STUDIO_FOR_TEAMS,
  ACROBAT_STUDIO_FOR_EDUCATION_HED_STUDENT,
  ACROBAT_STUDIO_FOR_EDUCATION_HED_FACULTY_STAFF,
  ACROBAT_STUDIO_FOR_ENTERPRISE,
];

const NON_DC_SUBSCRIPTION_CODES = ['ADOBE_EXPRESS'];

export const USER_TAG = {
  ageGroupUnder13: 'agegroup_under13',
  ageGroup13To15: 'agegroup_13_15',
  ageGroup16To17: 'agegroup_16_17',
  ageGroup18Plus: 'agegroup_18plus',
  ageGroupUnknown: 'agegroup_unknown',
  eduK12: 'edu_k12',
  eduStudent: 'edu_student',
};

const STUDENT_PRODUCT_ARRANGEMENT_CODES = [
  'ccsn_direct_individual',
  'acrobat_pro_dc_for_ste_individual',
];

const FEATURE_ORDER = [
  { name: DC_GLOBAL, level: PREMIUM_PLUS, order: 5 },
  { name: DC_ENTERPRISE, level: PREMIUM_BASIC, order: 10 },
  { name: ACROBAT_PLUS, level: PLUS, order: 20 },
  { name: ACROBAT_STD, level: BASIC, order: 30 },
  { name: PDF_PACK, level: PLUS, order: 40 },
  { name: DC_LITE, level: PLUS, order: 50 },
  { name: SCAN_PLUS, level: PLUS, order: 60 },
  { name: PDF_PACK, level: BASIC, order: 70 },
  { name: EDIT_PDF, level: BASIC, order: 80 },
  { name: OCR_PDF, level: BASIC, order: 90 },
  { name: EXPORT_PDF, level: BASIC, order: 100 },
  { name: E_SIGN, level: PREMIUM_PLUS, order: 110 },
  { name: E_SIGN, level: PREMIUM_BASIC, order: 120 },
  { name: E_SIGN, level: PLUS, order: 130 },
  { name: E_SIGN, level: BASIC, order: 140 },
  { name: SEND_AND_TRACK, level: PLUS, order: 150 },
  { name: CREATE_PDF, level: BASIC, order: 160 },
  { name: CREATE_PDF, level: FREE, order: 170 },
  { name: GENAI_SERVICES, level: BASIC, order: 180 },
  { name: GENAI_SERVICES, level: TRIAL, order: 190 },
  { name: ACROBAT_PLUS, level: FREE, order: 200 },
  { name: ACROBAT_STD, level: FREE, order: 210 },
  { name: PDF_PACK, level: FREE, order: 220 },
  { name: EXPORT_PDF, level: FREE, order: 230 },
  { name: E_SIGN, level: FREE, order: 240 },
  { name: FILES, level: FREE, order: Infinity - 10 },
  { name: PDF_SERVICES, level: FREE, order: Infinity },

];
// Convert FEATURE_ORDER to a map for quick lookup
const orderMap = new Map(FEATURE_ORDER.map(item => [`${item.name.toLowerCase()}_${item.level.toLowerCase()}`, item.order]));

export const BLOCK_LISTED_COUNTRY_CODES = ['CN', 'BY', 'CU', 'IR', 'KP', 'RU', 'SY'];

export interface ISubscription {
  name?: string;
  code?: string;
  display_name?: string;
  features?: any;
  level?: string;
  status?: string;
  sub_ref?: string;
  biz_source?: string;
  billing_term?: string;
  subscription_params?: any;
  [key: string]: any;
}

export interface IUserContent {
  subscriptions_v2?: {
    subscriptions?: ISubscription[];
  };
  subscriptionsV2?: {
    subscriptions?: ISubscription[];
  };
  subscriptions?: {
    subscriptions?: ISubscription[];
  };
  identity?: {
    name?: string;
    last_name?: string;
    country_code?: string;
    display_name?: string;
    email?: string;
    mrkt_perm_email?: boolean;
    first_name?: string;
    category?: string;
    language?: string;
    first_time?: object,
    user_id?: string;
    analytics_plan_code?: string;
    cohort?: string[];
    [key: string]: any;
  }
}

let userPromise;
let user: IUserContent = {};
let dcapi;
let dcapiPromise;
let esignPromise;
let sendPromise;
let baseUrisPromise;
let accessTokenPromise;
let genAIFeatureLimitsPromise;
let genAIProvisionStatusPromise;
let userGeoPromise;
let isGenAIEligiblePromise;
let genAIUriPromiseMap: { [key: string]: Promise<any> } = {};
let genAIUriExpiryMap: { [key: string]: number } = {};
let useLimitsCache = true;

export const isPrimitiveValue = value => Object(value) !== value;

function snakeToCamel(str) {
  if (!str) return str;
  const camelCaseStr = str.toLowerCase().replace(/([-_][a-z])/g, group => group
    .toUpperCase()
    .replace('-', '')
    .replace('_', ''),
  );
  return camelCaseStr?.charAt(0).toUpperCase() + camelCaseStr?.slice(1);
}

const getDcapi = () => {
  if (dcapiPromise) {
    return dcapiPromise;
  }
  dcapiPromise = DcapiAPI.getInstance()
    .then(dcapiInstance => dcapiInstance.getDcapi())
    .catch(e => {
      dcapiPromise = undefined;
      throw e;
    });
  return dcapiPromise;
};

const setUser = (content?: IUserContent) => {
  if (!content) {
    // if no content reset user promise
    userPromise = undefined;
    return content;
  }
  user = content;
  // remove prefs content that no longer fits our schema
  const dcwebPrefs = user['prefs/dcweb'];
  user.identity = user.identity || {};
  if (dcwebPrefs && dcwebPrefs.dcweb) {
    const fte = dcwebPrefs.dcweb.fte;
    if (fte) {
      delete fte.a11y;
      delete fte.launch_count;
    }
    const tour = dcwebPrefs.dcweb.coach_mark_tour;
    if (tour) {
      Object.keys(tour)
        .forEach(t => {
          delete tour[t].lastDateSeen;
          delete tour[t].timesSeen;
        });
    }
    user.identity = Object.assign(user.identity, { first_time: { dex_web_app: false } });
  } else {
    user.identity = Object.assign(user.identity, { first_time: { dex_web_app: true } });
  }
  return user;
};

const sortFeatures = (features = []) => {
  // Sort the features array based on the order values from the map
  features.sort((a, b) => {
    if (!a && !b) return 0;
    if (!a) return 1;
    if (!b) return -1;
    const keyA = `${a.name?.toLowerCase()}_${a.level?.toLowerCase()}`;
    const keyB = `${b.name?.toLowerCase()}_${b.level?.toLowerCase()}`;
    const orderA = orderMap.get(keyA) || Infinity; // Default to Infinity if not found
    const orderB = orderMap.get(keyB) || Infinity; // Default to Infinity if not found
    return orderA - orderB;
  });
};

const sortSubscriptionsV2 = subscriptions => {
  subscriptions.sort((a, b) => {
    const firstFeatureA = a.features?.[0];
    const firstFeatureB = b.features?.[0];

    // If either subscription has no features, sort them to the end
    if (!firstFeatureA && !firstFeatureB) return 0;
    if (!firstFeatureA) return 1;
    if (!firstFeatureB) return -1;

    const keyA = `${firstFeatureA.name?.toLowerCase()}_${firstFeatureA.level?.toLowerCase()}`;
    const keyB = `${firstFeatureB.name?.toLowerCase()}_${firstFeatureB.level?.toLowerCase()}`;
    const orderA = orderMap.get(keyA) || Infinity; // Default to Infinity if not found
    const orderB = orderMap.get(keyB) || Infinity; // Default to Infinity if not found
    return orderA - orderB;
  });
};

const sortSubscriptions = subscriptions => {
  subscriptions.sort((a, b) => {
    const keyA = `${a.name.toLowerCase()}_${a.level.toLowerCase()}`;
    const keyB = `${b.name.toLowerCase()}_${b.level.toLowerCase()}`;
    const orderA = orderMap.get(keyA) || Infinity; // Default to Infinity if not found
    const orderB = orderMap.get(keyB) || Infinity; // Default to Infinity if not found
    return orderA - orderB;
  });
};

const getAndAddMeterIQResponseToUser = async (userObj: IUser) => {
  if (isMeterIQEnabled()) {
    try {
      // Fetch limits from MeterIQ service MeterIQLimitsProvider
      const meterIQLimits = await MeterIQLimitsProvider.getInstance();
      const limits = await meterIQLimits.getLimits(useLimitsCache);
      userObj['limits/verbs'] = limits as unknown as IUser['limits/verbs']; // add limits/verbs to the user object for backward compatibility
      userObj['limits/conversions'] = meterIQLimits.transformMeterIQToConversions(limits); // add limits/conversions to the user object for backward compatibility
      useLimitsCache = true;
      return userObj;
    } catch (error) {
      logger.error('[UserAPI] Failed to fetch MeterIQ limits:', error);
      // let the UserAPI.getUser() promise to fail as it would have happened if MeterIQ was not enabled
      throw error;
    }
  }
  return userObj;
};

const getContentV2 = (content: IUserContent) => {
  const useSubV2 = window.adobe_dc_sdk?.launchOptions?.subV2;
  if (useSubV2) {
    const { subscriptions_v2, ...rest } = content;
    const contentV2 = { ...rest };
    contentV2.subscriptions = { subscriptions: [] };
    const subsV2 = subscriptions_v2?.subscriptions || [];
    // V1 Subscriptions , remove this code after migrating to v2 subscriptions
    contentV2.subscriptions.subscriptions = subsV2.flatMap(sub => {
      let features = sub.features;
      if (sub.features?.length > 1) {
        features = sub.features.filter(f => f?.name?.toLowerCase() !== 'pdfservices');
      }

      sortFeatures(features);
      return features?.map(({ params, ...f }) => ({
        ...f,
        code: sub.code,
        level: f?.level || 'Free',
        name: f?.name?.toLowerCase() === 'pdfservices' ? 'Files' : f?.name,
        status: 'Active',
        sub_ref: '',
        biz_source: '',
        billing_term: null,
        subscription_params: Object.entries(params || {}).reduce((acc, [key, value]) => {
          acc[snakeToCamel(key)] = Array.isArray(value) ? value.join(':') : value;
          return acc;
        }, {}),
      }));
    });
    sortSubscriptions(contentV2.subscriptions.subscriptions);
    // V2 subscriptions , keep this after migrating to v2 subscriptions
    // since all free and paid features have pdfservices with out level , it will be hard to distinguish free vs paid user
    // so manipulating the features to have pdfservices with level as Free for free users, ignore pdf services for paid pack.
    // obviously paid pack features length will more than 1 that includes pdfservices
    contentV2.subscriptionsV2 = { subscriptions: [] };
    contentV2.subscriptionsV2.subscriptions = subsV2.map(sub => {
      const {
        display_name,
        code,
        family,
        features = [],
      } = sub;
      let newFeatures = features;
      if (features?.length > 1) {
        newFeatures = features.filter(f => f?.name?.toLowerCase() !== 'pdfservices');
      }
      sortFeatures(newFeatures);
      return {
        code,
        display_name,
        family,
        features: newFeatures.map(f => {
          if (f?.name?.toLowerCase() === 'pdfservices' && f?.level === undefined) {
            f.level = 'Free';
          }
          return f;
        }),
      } as ISubscription;
    });
    sortSubscriptionsV2(contentV2.subscriptionsV2.subscriptions);
    return setUser(contentV2);
  }
  if (content?.subscriptions?.subscriptions) {
    return setUser(content);
  }
  // as a last resort
  throw new Error('Invalid user content without any subscriptions');
};

/**
 * @method
 * @returns {object} - success - user aggregated info
 *                     failure - Error
 * @private
 */
const getUser = async () => {
  if (!userPromise) {
    const options: { [key: string]: any } = {
      accept: CONTENT_TYPE_USER,
    };
    const useSubV2 = window.adobe_dc_sdk?.launchOptions?.subV2;
    const enableMeterIQ = isMeterIQEnabled();
    const sub = useSubV2 ? 'subscriptions_v2' : 'subscriptions';
    const fields = [
      'identity',
      'limits/acrobat',
      'limits/pdf_services',
      'limits/esign',
      ...(!enableMeterIQ ? ['limits/verbs', 'limits/conversions'] : []),
      'limits/express',
      'prefs/dcweb',
      'prefs/recent_assets',
      'prefs/recent_assets_timestamp',
      'prefs/common',
      sub,
    ];

    const getUserPromise = async () => {
    // Wait for auth state to be determined before checking sign-in status
      if (enableMeterIQ) {
        await waitForAuthState();
        const isSignedIn = auth2.isSignedIn;
        if (!isSignedIn) {
          // Get locale from window.adobe_dc_sdk.dom.getLocale() if available
          // This avoids circular dependency with Locale2API while still using actual locale
          const locale = (typeof window !== 'undefined' && window.adobe_dc_sdk?.dom?.getLocale)
            ? window.adobe_dc_sdk.dom.getLocale()
            : 'en-US';
          return getAndAddMeterIQResponseToUser(createMockAnonUser(locale));
        }
      }

      const requestAccessAppIds = getEnvVar('request_access_app_ids');
      if (requestAccessAppIds) {
        const requestAccessField = encodeURIComponent(`request_provisioning?appIds=${requestAccessAppIds}`);
        fields.push(requestAccessField);
      }

      options.uri_parameters = {
        fields: fields.join(','),
      };

      return getDcapi().then(api => {
        if (!dcapi) {
          dcapi = api;
        }
      }).then(() => dcapi.call(OPERATION_USER_GET, options))
        .then(({ content }) => getContentV2(content))
        .then((userObj: IUser) => getAndAddMeterIQResponseToUser(userObj)); // always fetch latest limits from MeterIQ service
    };
    // get user promise synchronously with fallback to mock anonymous user if not signed in
    // which requires auth state to be determined before checking sign-in status
    userPromise = getUserPromise().catch(e => {
      userPromise = undefined;
      throw e;
    });
  }
  return userPromise;
};

/**
 * @method
 * @returns {object} - success - user aggregated info
 *                     failure - Error
 * @private
 */
const getLimitsEsign = async () => {
  if (!esignPromise) {
    esignPromise = getDcapi().then(api => {
      if (!dcapi) {
        dcapi = api;
      }
    }).then(() => dcapi.call(OPERATION_LIMITS_ESIGN, {
      accept: CONTENT_TYPE_LIMITS_ESIGN,
    }))
      .then(({ content }) => content)
      .catch(e => {
        esignPromise = undefined;
        throw e;
      });
  }
  return esignPromise;
};

/**
 * @method
 * @returns {object} - success - the bearer access token or the given default if missing
 *                     failure - Error
 * @private
 */
const getAccessToken = (defaultIfMissing?: string) => {
  if (!accessTokenPromise) {
    accessTokenPromise = auth2.getSessionToken()
      .then(({ content }) => {
        if (!content.access_token) {
          if (defaultIfMissing !== undefined) {
            accessTokenPromise = undefined;
            return defaultIfMissing;
          }
          throw new ReferenceError('Missing access_token');
        }
        return `Bearer ${content.access_token}`;
      })
      .catch(e => {
        accessTokenPromise = undefined;
        throw e;
      });
  }
  return accessTokenPromise;
};

/**
 * @method
 * @returns {object} - success - the send and track base uris
 *                     failure - Error
 * @private
 */
const getSendAndTrackUris = () => {
  if (!baseUrisPromise) {
    const fileTierBaseUri = getEnvVar('files_uri');
    if (!fileTierBaseUri) {
      return Promise.reject(new ReferenceError('missing files_uri'));
    }
    const headers: { [header: string]: string } = {
      Accept: 'application/vnd.adobe.dex+json;version=1',
      'x-api-client-id': 'api_browser',
      'x-api-app-info': 'dc-web-app',
    };

    baseUrisPromise = getAccessToken(null)
      .then(token => {
        if (token) {
          headers.Authorization = token;
        }
      })
      .then(() => http.get(`${fileTierBaseUri}/api/base_uris`, { headers }))
      .then(({ content }) => JSON.parse(content))
      .catch(e => {
        baseUrisPromise = undefined;
        throw e;
      });
  }
  return baseUrisPromise;
};

/**
 * @method
 * @returns {object} - success - limits info for track and send
 *                     failure - Error
 * @private
 */
const getLimitsSend = () => {
  if (!sendPromise) {
    sendPromise = Promise.all([getSendAndTrackUris(), getAccessToken()])
      .then(([baseUris, accessToken]) => {
        const headers = {
          Accept: 'application/vnd.adobe.dex+json;version=1',
          Authorization: accessToken,
          'x-api-client-id': 'api_browser',
          'x-api-app-info': 'dc-web-app',
        };
        return http.get(`${baseUris.send_api}users/me/limits`, { headers }) as { content: string };
      }).then(({ content }) => JSON.parse(content))
      .catch(e => {
        sendPromise = undefined;
        throw e;
      });
  }
  return sendPromise;
};

export interface ILimits {
  [op: string]: {
    remaining: number;
    limit: number;
    [key: string]: number;
  }
}
export interface IExpressLimits {
  subscription_level: 'PAID' | 'FREE' | 'NONE';
}
export interface IFeature {
  name: string;
  level: string;
  params: {
    [key: string]: string;
  }
}

export interface IGenAIUri {
  url: string;
  additionalHeaders: IHttpHeaders;
}

export interface IResetOptions {
  shouldTriggerGcmEvent?: boolean;
}

export enum PROVISIONING_TYPE {
  INSTANT_PROVISIONING = 'INSTANT_PROVISIONING',
  WAITLIST = 'WAITLIST'
}

export enum SERVICE_VARIANT {
  dev = 'dev',
  beta = 'beta'
}

export interface IUserAPI {
  ready: () => Promise<IUserAPI | any>;
  clear: () => IUserAPI | any;
  getUser: () => Promise<IUser>;
  getLimitsEsign: () => Promise<string>;
  getLimitsSend: () => Promise<any>;
  isOriginalSharingEnabled: () => Promise<any>;
  getFilesTierBaseUris: () => Promise<any>;
  getIdentity: () => Promise<string>;
  getSubscriptions: () => Promise<string[]>;
  getSubscriptionsV2: () => Promise<string[]>;
  canEdit: () => Promise<boolean>;
  canExport: () => Promise<boolean>;
  canCreate: () => Promise<boolean>;
  canCombine: () => Promise<boolean>;
  canOrganize: () => Promise<boolean>;
  canSplit: () => Promise<boolean>;
  canCompress: () => Promise<boolean>;
  canCrop: () => Promise<boolean>;
  isFreeUser: () => Promise<boolean>;
  isFreeUserForAllExceptGenAIServices: () => Promise<boolean>;
  getLimitsConversions: () => Promise<ILimits>;
  getLimitsAcrobat: () => Promise<ILimits>;
  getLimitsVerbs: () => Promise<ILimits>;
  getLimitsExpress: () => Promise<IExpressLimits>;
  getPreferences: (category: string) => Promise<object>;
  setPreferences: (category: string, preferences: any) => Promise<object>;
  hasFeatures: (features: string[]) => Promise<boolean>;
  hasOnlyFeature: (feature: IFeature) => Promise<boolean>;
  getStorageType: () => Promise<string>;
  getGenAIServices: (serviceVariant: boolean | SERVICE_VARIANT) => Promise<string>;
  getGenAIProvisionUri: (operationName: string, useV2: boolean, serviceVariant: boolean | SERVICE_VARIANT) => Promise<IGenAIUri>;
  getGenAIFeatureLimits: (useCache: boolean, serviceVariant: boolean | SERVICE_VARIANT) => Promise<any>;
  getUserGenAIProvisionStatus: (useCache?: boolean, useV2?: boolean, serviceVariant?: boolean | SERVICE_VARIANT, clientHeaders?: object) => Promise<object>;
  provisionUserForGenAI: (provisioningType: PROVISIONING_TYPE, useV2?: boolean, serviceVariant?: boolean | SERVICE_VARIANT) => Promise<object>;
  getUserGeoLocation: () => Promise<object>;
  fetchUserProfilePicture: (userId: string) => Promise<any>;
  getUserProfilePicture: (userId: string) => Promise<any>;
  getIsGenAIEligible: (useCache?: boolean, useV2?: boolean, checkIfNonType1UserGenAIEligible?: (...args) => boolean) => Promise<object>;
  reset: (options?: IResetOptions) => Promise<void>;
  isVIPAdmin: () => Promise<boolean>;
  isAdmin: () => Promise<boolean>;
  isTrialUser: () => Promise<boolean>;
  isStudentAccount: () => Promise<boolean>;
}
export interface IUserAPIConfig {
  userDataPromise?: Promise<any>;
}

/**
 * @classdesc
 * Service provider with access to DCAPI
 * Wraps key user info DCAPI calls
 * @class
 */
class UserAPI implements IUserAPI {
  static getInstance;
  /**
  * Constructor for UserAPI
  * @constructor
  * @param {object} [config={}] - configuration object
  * @param {Promise} [config.userDataPromise] - Promise that resolves to user data
  * @returns {UserAPI} - instance of UserAPI
  * @public
  */
  constructor({ userDataPromise }: IUserAPIConfig = {}) {
    const self = this;
    // eslint-disable-next-line no-undef
    window.addEventListener('dc.imslib.user_profile_updated', () => {
      self.clear();
    });

    if (userDataPromise) {
      userPromise = userDataPromise;
    } else {
      // eslint-disable-next-line no-undef
      const appDataPromise = window.adobe_dc_sdk?.appDataReady;
      if (appDataPromise) {
        userPromise = appDataPromise.promise.then(appData => {
          if (appData?.data?.users?.identity) {
            return getContentV2(appData.data.users);
          }
          // user data not available. Reset userPromise
          userPromise = undefined;
          return undefined;
        }).catch(() => {
          userPromise = undefined;
        });
      }
    }
  }

  /** Instance of dcapi client to use */
  dcapi = undefined;
  profilePictureMap;
  activeABPSubscriptions;
  abpSubscriptionsPromise;

  /**
   * @description
   * Standard provider ready() method to allow lazy instantiation of API.
   * @method
   * @returns {Promise} - promise that resolves when user provider has been instantiated
   */
  ready() {
    return Promise.resolve(this);
  }

  /**
   * @description
   * Discard all cached user information in this provider
   *
   * @returns {object} returns a reference to this provider
   * @method
   */
  clear() {
    userPromise = undefined;
    useLimitsCache = false;
    esignPromise = undefined;
    sendPromise = undefined;
    baseUrisPromise = undefined;
    accessTokenPromise = undefined;
    genAIFeatureLimitsPromise = undefined;
    genAIProvisionStatusPromise = undefined;
    userGeoPromise = undefined;
    isGenAIEligiblePromise = undefined;
    this.profilePictureMap = undefined;
    genAIUriPromiseMap = {};
    genAIUriExpiryMap = {};
    this.abpSubscriptionsPromise = undefined;
    this.activeABPSubscriptions = undefined;
    return this;
  }

  /**
   * @description
   * Resets the provider info by discarding all cached user information and making a fresh API call
   * @param {object} [options={}] - options object containing info such as the resetType
   * @method
   */
  async reset(options: IResetOptions = {}) {
    const { shouldTriggerGcmEvent = false } = options;
    this.clear();
    await this.getUser();
    if (shouldTriggerGcmEvent) {
      await globalContextManager.emitProviderChange('user', { type: 'RESET' });
    }
  }

  /**
   * @method
   * @returns {object} - success - user aggregated info
   *                     failure - Error
   * @public
   */
  getUser() {
    return getUser().then(userObj => {
      userObj = { ...userObj };
      delete userObj['prefs/dcweb'];
      return userObj;
    });
  }

  /**
   * @method
   * @returns {object} - success - esign limits
   *                     failure - Error
   * @public
   */
  getLimitsEsign() {
    return getLimitsEsign().then(limits => limits.esign_access);
  }

  /**
   * @method
   * @returns {object} - success - send and track limits
   *                     failure - Error
   * @public
   */
  getLimitsSend() {
    return getLimitsSend();
  }

  /**
   * @method
   * @returns {object} - success
   *                        true - if original sharing is enable
   *                        false - otherwise
   *                     failure - if error occurs while fetching limits
   * @public
   */
  isOriginalSharingEnabled() {
    return getLimitsSend()
      .then(limits => limits.original_sharing_enabled)
      .catch(() => false);
  }

  /**
   * @method
   *
   * Get base uris for various services
   *
   * @returns {Promise<object>} returns a promise that resolves with
   * base uris of various services
   *
   * @public
   */
  getFilesTierBaseUris() {
    return getSendAndTrackUris();
  }

  /**
   * @method
   * @returns {object} -  success - user identity
   *         failure - Error
   * @public
   */
  getIdentity() {
    return getUser().then(({ identity }) => identity);
  }

  /**
   * @method
   * @returns {object} -  success - user identity
   *         failure - Error
   * @public
   */
  getSubscriptions() {
    return getUser().then(({ subscriptions }) => subscriptions);
  }

  /**
   *
   * @returns {object} -  success - user subscriptions in v2 schema
   */
  getSubscriptionsV2() {
    return getUser().then(({ subscriptionsV2 }) => subscriptionsV2);
  }

  /**
   * @method
   * @returns {boolean} -  if user is able to edit pdf
   * @public
   */
  canEdit() {
    return getUser().then(res => res['limits/conversions'].edit_pdf_ops.remaining !== 0);
  }

  /**
   * @method
   * @returns {boolean} -  User does not have 0 remaining conversions
   * @public
   */
  canExport() {
    return getUser().then(res => res['limits/conversions'].export_pdf_conversions.remaining !== 0);
  }

  /**
   * @method
   * @returns {boolean} -  User does not have 0 remaining conversions
   * @public
   */
  canCreate() {
    return getUser().then(res => res['limits/conversions'].create_pdf_conversions.remaining !== 0);
  }

  /**
   * @method
   * @returns {boolean} -  User does not have 0 remaining conversions
   * @public
   */
  canCombine() {
    return getUser().then(res => res['limits/conversions'].combine_pdf_conversions.remaining !== 0);
  }

  /**
   * @method
   * @returns {boolean} -  User does not have 0 remaining conversions
   * @public
   */
  canOrganize() {
    return getUser().then(res => res['limits/conversions'].organize_pdf_conversions.remaining !== 0);
  }

  /**
   * @method
   * @returns {boolean} -  User does not have 0 remaining conversions
   * @public
   */
  canSplit() {
    return getUser().then(res => res['limits/conversions'].split_pdf_conversions.remaining !== 0);
  }

  /**
   * @method
   * @returns {boolean} -  User does not have 0 remaining conversions
   * @public
   */
  canCompress() {
    return getUser().then(res => res['limits/conversions'].optimize_pdf_ops.remaining !== 0);
  }

  /**
   * @description
   * Gets the user's conversions limits (file size limit, conversions remaining, etc)
   * Schema: https://dc-api-dev.adobe.io/schemas/user_limits_conversions_v1.json
   * @method
   * @returns {object} -  User's conversion limits
   * @public
   */
  getLimitsConversions() {
    return getUser().then(res => res['limits/conversions']);
  }

  /**
   * @method
   * @returns {object} -  User's acrobat limits, e.g. {acrobat_std, acrobat_pro}
   * @public
   */
  getLimitsAcrobat() {
    return getUser().then(res => res['limits/acrobat']);
  }

  /**
   * @description
   * Gets the limits for each verb
   * Schema: https://dc-api-dev.adobe.io/schemas/user_limits_conversions_v1.json
   * @method
   * @returns {object} -  User's conversion limits
   * @public
   */
  getLimitsVerbs() {
    return getUser().then(res => res['limits/verbs']);
  }

  /**
   * @description
   * Gets the limits for Adobe express
   * Schema: https://dc-api-dev.adobe.io/schemas/user_limits_express_v1.json
   * @method
   * @returns {object} -  User's express limits
   * @public
   */
  getLimitsExpress() {
    return getUser().then(res => res['limits/express']);
  }

  /**
   * @method
   * @returns {boolean} -  if user is able to crop pdf
   * @public
   */
  canCrop() {
    return getUser().then(res => res['limits/verbs']['crop-pages'].limits.remaining !== 0);
  }

  /**
   * @description
   * Gets the values associated with the specified user preference category. If the category
   * is a slash-delimited path (eg. dcweb/fte), this will return only the specified subset. If
   * the subset cannot be found for a valid category, this returns <code>undefined</code>.
   * @method
   * @param {string} category - name of preference category to fetch. This value can also be
   *                            a path (delimited by "/") to a preference subtree or specific
   *                            value.
   *
   * @returns {object} - success - The specified preference(s)
   *         failure - Error
   * @public
   */
  getPreferences(category: string) {
    const [mainCategory, ...path] = category.split('/');

    return getUser().then(userObj => {
      // use the cached user, in case setPreferences has
      // been called.
      const userPrefs = userObj[`prefs/${mainCategory}`]
        ? userObj[`prefs/${mainCategory}`][mainCategory] : undefined;
      if (!path || path.length === 0) {
        return userPrefs;
      }
      // This will iterate over the path segments to traverse the preference data/tree.
      return path.reduce((prefs, name) => {
        if (prefs && name in prefs) {
          return prefs[name];
        }
        return undefined;
      }, userPrefs);
    });
  }

  /**
   * @description
   * Updates the values assocated with the specified user preference category.
   * @method
   * @param {string} category - name of preference category to set.
   *
   * @param {(object|string)} preferences - Object/String with all or a preference for given category. This
   *                               will completely replace the current category's
   *                               preferences.
   *
   * @returns {Promise} - If successful this resolves without a value. If there is
   *                     a failure, the promise will be rejected with an error object.
   * @public
   */
  setPreferences(category, preferences) {
    const [mainCategory, ...remainingCategories] = category.split('/');

    return this.getPreferences(mainCategory)
      .then((fullPreferences = {}) => {
        const oldPreferences = JSON.parse(JSON.stringify(fullPreferences));
        if (remainingCategories && remainingCategories.length >= 1) {
          let lastLevel = null;
          let lastKey = null;
          remainingCategories.reduce((userPreferences, currentPref) => {
            lastLevel = userPreferences;
            lastKey = currentPref;
            return userPreferences[currentPref]
              ? userPreferences[currentPref] : userPreferences[currentPref] = {};
          }, fullPreferences);
          if (isPrimitiveValue(preferences)) {
            lastLevel[lastKey] = preferences;
          } else if (Array.isArray(preferences)) {
            lastLevel[lastKey] = [...preferences];
          } else {
            lastLevel[lastKey] = { ...lastLevel[lastKey], ...preferences };
          }
        } else {
          // there is no path in the category, modify the main category
          // We shouldn't be allowing primitive values for top level pref (dcweb)
          // This block will be removed in the future
          // eslint-disable-next-line
          if (isPrimitiveValue(preferences)) {
            fullPreferences = preferences;
          } else if (Array.isArray(preferences)) {
            fullPreferences = [...preferences];
          } else {
            fullPreferences = { ...fullPreferences, ...preferences };
          }
        }

        return getUser().then(async userObj => {
          if (!userObj[`prefs/${mainCategory}`]) {
            userObj[`prefs/${mainCategory}`] = {};
          }
          user[`prefs/${mainCategory}`][mainCategory] = fullPreferences;
          if (!dcapi) {
            dcapi = await getDcapi();
          }
          return dcapi.call(OPERATION_PREFS_PUT, {
            content_type: CONTENT_TYPE_USER_PREFS,
            uri_parameters: {
              category: mainCategory,
            },
            content: {
              [mainCategory]: fullPreferences,
            },
          }).then(res => {
            user[`prefs/${mainCategory}`][mainCategory] = fullPreferences;
            return res;
          }).catch(e => {
            // revert changes in case of failure
            user[`prefs/${mainCategory}`][mainCategory] = oldPreferences;
            throw e;
          });
        });
      });
  }

  /**
   * @method
   * @returns {boolean} - true if user has only Free level subscriptions
   *         failure - Error
   * @public
   */
  isFreeUser() {
    return this.getSubscriptions().then(subscriptions => subscriptions.subscriptions.every(sub => sub.level === 'Free'));
  }

  /**
   * @method
   * @returns {boolean} - true if user has only Free level subscriptions excluding GenAIServices subscription
   *         failure - Error
   * @public
   */
  isFreeUserForAllExceptGenAIServices() {
    return this.getSubscriptions().then(
      subscriptions => subscriptions.subscriptions.every(sub => sub.level === 'Free' || sub.name === 'GenAIServices'));
  }

  /**
   * @method
   * features: Array of features with param (experience) to check for
   * for example : features  = [{name: 'GenAIServices', level: 'Basic', params: {acrobat_experience: ['ACRO_EXP_HED']}}]
   * @returns {boolean} - true if user has all the features
   *         failure - Error
   * @public
   */
  hasFeatures = async features => {
    if (!auth2.isSignedIn || !features || !Array.isArray(features)) {
      return false;
    }
    const subs = await this.getSubscriptionsV2();
    if (!Array.isArray(subs?.subscriptions)) {
      return false;
    }
    // Filter out subscriptions that have non-free features
    const nonFreeSubs = subs.subscriptions.filter(sub => sub.features.some(f => f?.level?.toLowerCase() !== 'free'));
    return features.every(feature => {
      if (!feature?.name) {
        return false;
      }
      const subscription = nonFreeSubs.find(sub => sub?.features?.find(f => (f?.name?.toLowerCase() === feature?.name?.toLowerCase() && (feature?.level ? f?.level?.toLowerCase() === feature?.level?.toLowerCase() : true))));
      if (!subscription) {
        return false;
      }
      if (!feature?.params && subscription) {
        return true;
      }

      if (feature.params && subscription) {
        const subFeature = subscription.features.find(f => f?.name?.toLowerCase() === feature?.name?.toLowerCase());
        if (subFeature) {
          const subParams = subFeature.params;
          if (subParams) {
            return Object.keys(feature?.params).every(key => {
              const value = feature?.params[key];
              if (Array.isArray(value)) {
                return value.every(val => subParams[key]?.includes(val));
              }
              return subParams[key]?.includes(value);
            });
          }
        }
      }
      return false;
    });
  }

  /**
   * @method
   * feature: feature with param (experience) to check for
   * for example : feature  = {name: 'GenAIServices', params: {acrobat_experience: ['ACRO_EXP_HED']}}
   * @returns {boolean} - true if user has only the feature
   *         failure - Error
   * @public
   */
  hasOnlyFeature = async (feature: IFeature) => {
    if (!auth2.isSignedIn || !feature?.name) {
      return false;
    }
    const subs = await this.getSubscriptionsV2();
    if (!Array.isArray(subs?.subscriptions)) {
      return false;
    }
    // Filter out subscriptions that have non-free features
    const nonFreeSubs = subs.subscriptions.filter(sub => sub?.features?.some(f => f?.level?.toLowerCase() !== 'free'));

    if (nonFreeSubs.length > 1) {
      return false;
    }

    if (nonFreeSubs[0].features.lenth > 1
        || (nonFreeSubs[0].features[0]?.name?.toLowerCase() !== feature?.name?.toLowerCase()
          || (feature?.level && nonFreeSubs[0].features[0]?.level?.toLowerCase() !== feature?.level?.toLowerCase()))) {
      return false;
    }

    if (feature.params && nonFreeSubs[0]) {
      const subFeature = nonFreeSubs[0].features[0];
      if (subFeature) {
        const subParams = subFeature.params;
        if (subParams) {
          const isAvailable = Object.keys(feature?.params).every(key => {
            const value = feature?.params[key];
            if (Array.isArray(value)) {
              return value.every(val => subParams[key]?.includes(val));
            }
            return subParams[key]?.includes(value);
          });
          if (isAvailable) {
            return true;
          }
        }
      }
    }
    return false;
  }

  /**
   * @method
   * Determines the user's primary storage type
   * @returns Promise<{('ACP' | 'SC')}> - The user storage type which can be either ACP or SC
   * @public
   */
  async getStorageType() {
    if (!dcapi) {
      dcapi = await getDcapi();
    }
    return dcapi.templates.search_uri_primary === '{+search_uri_v2}' ? 'ACP' : 'SC';
  }

  /**
   * Reset DcAPi. Used only for test
   * @private
   */
  __resetDcApi() {
    dcapi = undefined;
    dcapiPromise = undefined;
  }

  _getGenAIProvisionAcceptHeader = operation => {
    const { accept } = operation;
    if (accept && (typeof accept === 'object') && (accept !== null)) {
      const { application_json_v1: applicationJsonV1AcceptHeader } = accept;
      if (applicationJsonV1AcceptHeader) {
        return applicationJsonV1AcceptHeader;
      }
    }
    return accept;
  }

  /**
   * @description
   * Calls the dc index uri to get the service details.
   * @method
   * @param {string} serviceVariant -Variant to service for which details would be fetched. Options: dev | beta.
   * If not provided release services are returned.
   * @returns {Promise} - If successful this resolves with the services object.
   * If there is a failure, the promise will be rejected with an error object.
   * @public
   */
  getGenAIServices = async (serviceVariant: boolean | SERVICE_VARIANT) => {
    if (!dcapi) {
      dcapi = await getDcapi();
    }

    const serviceVariantKey: string = typeof serviceVariant === 'boolean' ? `${serviceVariant}` : serviceVariant as string;

    const resourceUrl = dcapi.templates.dc_index_uri;
    const appInfo = dcapi.config['x-api-app-info'];
    const clientId = dcapi.config['x-api-client-id'];
    if (!resourceUrl) {
      throw new Error('dc_index_uri not found');
    }

    // Return cached promise if it exists and is still valid.
    const currentTs = Date.now() / 1000;
    if (genAIUriPromiseMap[serviceVariantKey] && currentTs < genAIUriExpiryMap[serviceVariantKey]) {
      return genAIUriPromiseMap[serviceVariantKey];
    }

    // Prepare request headers. Only attach the variant header for non-release variants.
    const authToken = auth2.getAccessTokenInfo().access_token;
    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
      'x-api-app-info': appInfo,
      'x-api-client-id': clientId,
      // Handling boolean value for backward compatibility of useBetaService flag
      ...(serviceVariant && { 'x-genai-service-variant': serviceVariant === true ? SERVICE_VARIANT.beta : serviceVariant }),
    };
    const response = await http.get(resourceUrl, { headers });
    if (response.status === 200) {
      const { expiry, services } = JSON.parse(response.content);
      genAIUriPromiseMap[serviceVariantKey] = new Promise(resolve => resolve(services));
      genAIUriExpiryMap[serviceVariantKey] = expiry;
      return services;
    }

    throw new Error(`Unable to fetch discoverable genai uri. Failed with status code:${response.status}`);
  }

  getGenAIProvisionUri = async (operationName: string, useV2: boolean, serviceVariant: boolean | SERVICE_VARIANT): Promise<IGenAIUri> => {
    const servicesObject = await this.getGenAIServices(serviceVariant);
    const clientId = dcapi.config['x-api-client-id'];
    if (servicesObject) {
      const service = useV2 ? servicesObject.gen_ai_provisioning_v2 : servicesObject.gen_ai_provisioning;
      if (service) {
        const operation = service.resources?.operations[operationName];
        if (operation && operation.uri && operation.resource_parameter) {
          const {
            uri, resource_parameter: resourceParameter,
          } = operation;
          const baseUri = resourceParameter.default;
          const urlTemplate = template.parse(uri);
          const url = urlTemplate.expand({ base_uri: baseUri });
          const additionalHeaders: IHttpHeaders = {
            // Handling boolean value for backward compatibility of useBetaService flag
            ...(serviceVariant && { 'x-genai-service-variant': serviceVariant === true ? SERVICE_VARIANT.beta : serviceVariant }),
          };
          if (useV2) {
            additionalHeaders['x-api-client-id'] = clientId;
            const accept = this._getGenAIProvisionAcceptHeader(operation);
            if (accept) additionalHeaders.Accept = accept;
          }
          return {
            url,
            additionalHeaders,
          };
        }
      }
    }
    throw new Error(`not able to fetch resource url for ${operationName}`);
  }

  /**
   * @description
   * Calls the Provision `limits` API to get the GenAI feature limits.
   * @method
   * @param {bool} useCache - If true, the cached value will be returned if available.
   * @param {string} serviceVariant -Variant to service for which details would be fetched. Options: dev | beta.
   * @returns {Promise} - If successful this resolves values
   *  {
   *    feature_limits: {
   *      allow_scanned_pdfs_enabled: true | false
   *      max_chars_per_question: <number>
   *      max_file_size: <number>
   *      max_page_count: <number>
   *      supported_doc_languages: ["en", "fr", ...]
   *    },
   *    expires_at: <number>
   *  }
   * If there is a failure, the promise will be rejected with an error object.
   * @public
   */
  getGenAIFeatureLimits(useCache = true, serviceVariant: boolean | SERVICE_VARIANT): Promise<any> {
    if (useCache && genAIFeatureLimitsPromise) {
      return genAIFeatureLimitsPromise;
    }

    if (!auth2.isSignedIn) {
      const errorObj = new Error('User is not Signed-In');
      genAIFeatureLimitsPromise = Promise.reject(errorObj);
      return genAIFeatureLimitsPromise;
    }

    genAIFeatureLimitsPromise = Promise.all([
      this.getGenAIProvisionUri('limits', true, serviceVariant),
      getAccessToken(null),
    ])
      .then(([{ url, additionalHeaders }, token]) => {
        const clientId = auth2.getClientID();
        const headers = {
          Accept: 'application/json',
          'x-api-key': clientId,
          ...(token && { Authorization: token }),
          ...additionalHeaders,
        };
        return http.get(url, { headers }) as Awaited<IHttpResponse>;
      })
      .then(response => {
        if (response.status === 200) {
          return JSON.parse(response.content);
        }
        const errorObj = new Error(`Failed to fetch disqualification limits: {"status": ${response.status},"statusText": "${response.statusText}"}`);
        throw errorObj;
      })
      .catch(error => {
        genAIFeatureLimitsPromise = null;
        throw error;
      });

    return genAIFeatureLimitsPromise;
  }

  /**
   * @description
   * Calls the Provision Status API to get the GenAI provision status for the user.
   * @method
   * @param {bool} useCache - If true, the cached value will be returned if available.
   * @param {bool} useV2 - If true, v2 of provisioning service will be used.
   * @param {string} serviceVariant -Variant to service for which details would be fetched. Options: dev | beta.
   * @returns {Promise} - If successful this resolves values
   *  {
   *      status: NOT_ELIGIBLE | CAN_SHOW_SIGNUP | CAN_SHOW_TRY_NOW | WAITLISTED | PROVISIONED
   *     profile: {account_type: "T1", customer_tyoe: "individual"}
   *     can_contact_admin: true | false
   * }
   * If there is a failure, the promise will be rejected with an error object.
   * @public
   */
  getUserGenAIProvisionStatus(useCache = true, useV2 = false, serviceVariant?: boolean | SERVICE_VARIANT, clientHeaders?: object) {
    if (useCache && genAIProvisionStatusPromise) return genAIProvisionStatusPromise;
    if (!auth2.isSignedIn) {
      const errorObj = new Error('User is not Signed-In');
      genAIProvisionStatusPromise = Promise.reject(errorObj);
      return genAIProvisionStatusPromise;
    }
    const clientId = auth2.getClientID();
    let headers: IHttpHeaders = {
      Accept: 'application/json',
      'x-api-key': clientId,
    };
    if (clientHeaders && typeof clientHeaders === 'object') {
      headers = { ...headers, ...clientHeaders };
    }
    genAIProvisionStatusPromise = this.getGenAIProvisionUri('status', useV2, serviceVariant).then(({ url, additionalHeaders }) => getAccessToken(null).then(token => {
      if (token) {
        headers.Authorization = token;
        headers = { ...headers, ...additionalHeaders };
      }
    })
      .then(() => {
        const genAIStatusUri = `${url}?group_name=gen_ai_initial`;
        return http.get(genAIStatusUri, { headers });
      })
      .then(response => {
        if (response.status === 200) {
          return JSON.parse(response.content);
        }
        const errorObj = new Error(`Provison Status Failed: {"status": ${response.status},"statusText": "${response.statusText}"}`);
        throw errorObj;
      }))
      .catch(err => {
        genAIProvisionStatusPromise = undefined;
        logger.error(`Provison Status API Failed with error code ${err?.code} due to %O`, err);
        throw err;
      });
    return genAIProvisionStatusPromise;
  }

  /**
   * @description
   * Calls the Provision API to Provision Server Resources for GenAI features.
   * @method
   * @param {string} - provisioningType - INSTANT_PROVISIONING | WAITLIST
   * @param {bool} - useV2 - If true, v2 of provisioning service will be used.
   * @param {string} serviceVariant -Variant to service for which details would be fetched. Options: dev | beta.
   * @returns {Promise} - If successful this resolves values
   *  If there is a failure, the promise will be rejected with an error object.
   * @public
   */
  provisionUserForGenAI(provisioningType: PROVISIONING_TYPE, useV2 = false, serviceVariant?: boolean | SERVICE_VARIANT) {
    if (!auth2.isSignedIn) {
      const errorObj = new Error('User is not Signed-In');
      return Promise.reject(errorObj);
    }
    const clientId = auth2.getClientID();
    let headers: IHttpHeaders = {
      Accept: 'application/json',
      'x-api-key': clientId,
      'Content-Type': 'application/json',
    };

    const body = JSON.stringify({
      provisioning_type: provisioningType,
      group_name: 'gen_ai_initial',
    });

    return this.getGenAIProvisionUri('provision', useV2, serviceVariant)
      .then(({ url, additionalHeaders }) => getAccessToken(null)
        .then(token => {
          if (token) {
            headers.Authorization = token;
            headers = { ...headers, ...additionalHeaders };
          }
        })
        .then(() => http.post(url, { headers }, body))
        .then(response => {
          if (response.status === 200) {
            return JSON.parse(response.content);
          }
          const errorObj = new Error(`Provisioning Failed: {"status": ${response.status},"statusText": "${response.statusText}"}`);
          throw errorObj;
        }));
  }

  /**
   * @description
   * Get geo location of the user
   * @method
   * @returns {Promise} - If successful this resolves with the geo data.
   * eg: { country: 'IN', state: 'UP', 'Accept-Language': 'en-US,en;q=0.9' }
   * If there is a failure, the promise will be rejected with an error object.
   * @public
   */
  getUserGeoLocation() {
    if (userGeoPromise) return userGeoPromise;
    const geoUri = 'https://geo-dc.adobe.com/json/';
    const headers: IHttpHeaders = {
      Accept: 'application/json',
    };
    userGeoPromise = http.get(geoUri, { headers })
      .then(response => {
        if (response.status === 200) {
          return JSON.parse(response.content);
        }
        const errorObj = new Error(`GeoLocation Failed: {"status": ${response.status},"statusText": "${response.statusText}"}`);
        throw errorObj;
      })
      .catch(err => {
        userGeoPromise = undefined;
        throw err;
      });
    return userGeoPromise;
  }

  _isNonType1UserGenAIEligible(useV2) {
    return this.getUserGenAIProvisionStatus(true, useV2).then(resp => {
      if (resp.status !== 'NOT_ELIGIBLE') {
        return true;
      }
      if (resp.can_contact_admin) {
        return true;
      }
      throw new Error('User is not eligible for GenAI');
    });
  }

  /**
   * @description
   * Get if User is eligible for GenAI features
   * @method
   * @param {useCache} - If true, the cached value will be returned if available.
   * @param {checkIfNonType1UserGenAIEligible} - A callback function that clients can pass to override eligibility checks for non-T1 users.
   * @returns {Promise} - Resolves true|false
   * Rejects with an error object with the reason for ineligibility
   * @public
   */

  getIsGenAIEligible(useCache = true, useV2 = false, checkIfNonType1UserGenAIEligible = useV2API => this._isNonType1UserGenAIEligible(useV2API)) {
    if (useCache && isGenAIEligiblePromise) return isGenAIEligiblePromise;
    isGenAIEligiblePromise = new Promise((resolve, reject) => {
      if (!auth2.isSignedIn) {
        return reject(new Error('User is not signed in'));
      }

      const supportedGenAILocales = ['en'];
      const userLocale = window.adobe_dc_sdk.dom ? window.adobe_dc_sdk.dom.getLocale() : 'en-US';
      const primaryLocale = userLocale.split('-')[0];
      const genAILocalesPromise = window.adobe_dc_sdk?.loaded?.genAILocalesPromise?.promise || Promise.resolve(supportedGenAILocales);

      const userProfile = auth2.getUserProfile();

      return genAILocalesPromise.then(genAILocales => {
        if (!genAILocales.includes(primaryLocale)) {
          return reject(new Error(`User locale is not supported : ${userLocale}`));
        }
        const getGeoLocationPromise = this.getUserGeoLocation();
        const genAIEligiblityStatusPromise = userProfile.account_type !== 'type1'
          ? checkIfNonType1UserGenAIEligible(useV2)
          : Promise.resolve(true);
        return Promise.all([getGeoLocationPromise, genAIEligiblityStatusPromise])
          .then(([geoData, genAIEligibilityStatus]) => {
            if (!geoData || BLOCK_LISTED_COUNTRY_CODES.includes(geoData.country)) {
              return reject(new Error(`User is from ${geoData.country} which is a blocked country`));
            }
            return resolve(genAIEligibilityStatus);
          }).catch(err => {
            isGenAIEligiblePromise = undefined;
            reject(err);
          });
      });
    });
    return isGenAIEligiblePromise;
  }

  /**
   * @description
   * Fetch profile picture of the user
   * @method
   * @returns {Promise} - If successful this resolves with the user's public profile images in different sizes.
   * If there is a failure, the promise will be rejected with an error object.
   * @public
   */
  async fetchUserProfilePicture(userID) {
    try {
      if (!auth2.isSignedIn) {
        throw new Error('User is not Signed-In');
      }
      const clientId = auth2.getClientID();
      const headers: IHttpHeaders = {
        Accept: 'application/json',
        'x-api-key': clientId,
      };
      const ppsBaseUri = getEnvVar('pps_uri');
      const token = await getAccessToken(null);
      if (token) {
        headers.Authorization = token;
      }
      const response = await http.get(`${ppsBaseUri}/api/profile/${userID}`, { headers });
      if (response.status === 200 || response.status === 203) {
        const content = JSON.parse(response.content);
        return content.images;
      }
      throw new Error(`Fetch User Profile Image Failed: {"status": ${response.status},"statusText": "${response.statusText}"}`);
    } catch (error) {
      this.profilePictureMap[userID] = null;
      throw error;
    }
  }

  /**
   * @description
   * Gets the profile picture of the user from the cache if present, else makes a GET request
   * @method
   * @returns {Promise} - If successful this resolves with the user's public profile images in different sizes.
   * If there is a failure, the promise will be rejected with an error object.
   * API doc: https://identity.corp.adobe.com/pps/docs/#/Images/getImage
   * @public
   */
  getUserProfilePicture(userID) {
    if (!this.profilePictureMap) {
      this.profilePictureMap = new Map();
    }
    if (this.profilePictureMap[userID]) {
      return this.profilePictureMap[userID];
    }
    const profilePicturePromise = this.fetchUserProfilePicture(userID);
    this.profilePictureMap[userID] = profilePicturePromise;
    return profilePicturePromise;
  }

  /**
   * Retrieves the ABP subscriptions for the signed-in user.
   * @returns {Promise<Array|undefined>} A promise that resolves to an array of subscriptions or undefined if the request is partially fulfilled.
   * @throws {Error} If the user is not signed in, if the subscription profile URI is missing, or if there is an HTTP error.
   * API doc: https://developers.corp.adobe.com/SubscriptionProfileService/docs/guide/introduction.md
   * @private
   */
  _getABPSubscriptions() {
    if (!this.abpSubscriptionsPromise) {
      this.abpSubscriptionsPromise = new Promise((resolve, reject) => {
        if (!auth2.isSignedIn) {
          return reject(new Error('User is not Signed-In'));
        }

        const subscriptionProfileURI = getEnvVar('subscription_profile_uri');
        if (!subscriptionProfileURI) {
          return reject(new ReferenceError('missing subscription_profile_uri'));
        }
        const subscrProfileURL = new URL(`${subscriptionProfileURI}/v1`);
        subscrProfileURL.searchParams.append(
          'include_sections',
          'OFFER_DETAILS,PRODUCT_ARRANGEMENTS,FULFILLABLE_ITEMS,CONTRACT_DETAILS',
        );
        subscrProfileURL.searchParams.append('include_inactive_subscriptions', 'false');
        subscrProfileURL.searchParams.append('include_disabled_fis', 'false');
        subscrProfileURL.searchParams.append('after_index', '0');
        subscrProfileURL.searchParams.append('limit', '100');
        subscrProfileURL.searchParams.append('include_derived_attributes', 'true');
        const xRequestId = uuidv4();
        const accessToken = auth2.getAccessTokenInfo().access_token;
        const headers = {
          Accept: 'application/json',
          'X-Api-Key': auth2.getClientID(),
          'X-Request-Id': xRequestId,
          Authorization: `Bearer ${accessToken}`,
        };

        http.get(subscrProfileURL.href, { headers })
          .then(response => {
            if (response.status < 200 || response.status > 299) {
              return reject(new Error(`HTTP error! Status: ${response.status}`));
            }
            if (response.status === 206) {
              logger.emit(
                'partialContent',
                { level: 'warn', 'x-request-id': xRequestId },
              );
              this.abpSubscriptionsPromise = undefined;
              return resolve(undefined);
            }
            const result = JSON.parse(response.content);
            return resolve(result?.commerce_profile?.subscriptions);
          })
          .catch(e => {
            logger.emit(
              (e && e.message) || 'unknown',
              { level: 'error', 'x-request-id': xRequestId },
            );
            this.abpSubscriptionsPromise = undefined;
            reject(e);
          });
      });
    }
    return this.abpSubscriptionsPromise;
  }

  /**
   * Retrieves active ABP subscriptions.
   * This method returns a promise that resolves to a filtered list of active ABP subscriptions.
   * The subscriptions are considered active if their status is 'ACTIVE' and they have offer details
   * with fulfillable items that have either PDF services or Acrobat GenAI services enabled.
   * @returns {Promise<Array>} A promise that resolves to an array of active ABP subscriptions.
   * @public
   */
  async getActiveABPSubscriptions() {
    if (!this.activeABPSubscriptions) {
      const subscriptions = await this._getABPSubscriptions();
      this.activeABPSubscriptions = subscriptions?.filter(s => {
        if (s?.status === 'ACTIVE' && s?.offer_details) {
          return s?.offer_details?.some(offer => offer?.product_arrangement?.fulfillable_items?.pdf_services?.enabled
              || offer?.product_arrangement?.fulfillable_items?.acrobat_genai_services?.enabled);
        }
        return false;
      });
    }
    return this.activeABPSubscriptions;
  }

  /**
   * Retrieves the product codes for active ABP offer subscriptions.
   * This method checks if there is an existing promise for active ABP subscriptions.
   * If not, it initiates the retrieval of active ABP subscriptions.
   * It then processes the subscriptions to extract and return the product codes.
   * @returns {Promise<string>} A promise that resolves to a comma-separated string of product codes.
   * @public
   */
  async getABPOfferProductCodes() {
    if (!this.activeABPSubscriptions) {
      await this.getActiveABPSubscriptions();
    }
    const subscriptions = this.activeABPSubscriptions;
    if (subscriptions?.length) {
      return subscriptions.map(s => {
        if (s.offer_details?.length) {
          return s.offer_details.map(o => o.product_arrangement?.code).join(',');
        }
        return undefined;
      }).filter(c => c !== undefined).join(',');
    }
    return '';
  }

  /**
   * Retrieves a comma-separated string of offer IDs from active ABP subscriptions.
   * If there are no active subscriptions or offer details, an empty string is returned.
   * @returns {Promise<string>} A promise that resolves to a comma-separated string of offer IDs.
   * @public
   */
  async getABPOfferIDs() {
    if (!this.activeABPSubscriptions) {
      await this.getActiveABPSubscriptions();
    }
    const subscriptions = this.activeABPSubscriptions;
    if (subscriptions?.length) {
      return subscriptions.map(s => {
        if (s.offer_details?.length) {
          return s.offer_details.map(o => o.offer_id).join(',');
        }
        return undefined;
      }).filter(c => c !== undefined).join(',');
    }
    return '';
  }

  /**
   * Checks if the user is currently in a trial period for any active ABP subscriptions.
   * @returns {Promise<boolean>} A promise that resolves to true if the user is in a trial period, otherwise false.
   * @public
   */
  async isTrialUser() {
    if (!this.activeABPSubscriptions) {
      await this.getActiveABPSubscriptions();
    }
    const subscriptions = this.activeABPSubscriptions;
    return subscriptions?.some(s => s.trial_details?.in_trial_period) || false;
  }

  /**
   * Checks ABP subscriptions to determine if user is VIP admin.
   * @returns {Promise<boolean>} A promise that resolves to true if the user is VIP Admin, otherwise false.
   * @public
   */
  async isVIPAdmin() {
    if (!this.activeABPSubscriptions) {
      await this.getActiveABPSubscriptions();
    }
    const subscriptions = this.activeABPSubscriptions;
    const isVipAdminUser = subscriptions?.some(subcription =>
      subcription?.offer_details?.some(offer => // eslint-disable-line implicit-arrow-linebreak
        (offer?.buying_program === 'VIP' || offer?.buying_program === 'VIPMP'))); // eslint-disable-line implicit-arrow-linebreak
    return isVipAdminUser;
  }

  /**
   * Checks IMS profile to determine if the user has admin status.
   * @returns {boolean} True if the user is an admin, otherwise false.
   * @public
   */
  async isAdmin() {
    const imsProfile = await auth2.getUserProfileAsync();
    const result = imsProfile?.roles?.findIndex(role => role?.['named_role'] === 'org_admin') > -1; // eslint-disable-line dot-notation
    return result;
  }

  /**
   * @method
   * @param {string} type - The type of studio user to check. Options: 'any', 'only', 'hed'. Default is 'any'.
   * @returns {Promise<boolean>} - true if user matches the specified studio type
   * @public
   */
  isStudioUser(type: 'any' | 'only' | 'hed' = 'any'): Promise<boolean> {
    return getUser().then(res => {
      const hasStudioExp = !!(res['prefs/common']?.common?.show_acrobat_studio_exp);
      const hasStudioHedExp = !!(res['prefs/common']?.common?.show_acrobat_studio_hed_exp);

      switch (type) {
      case 'only':
        return hasStudioExp && !hasStudioHedExp;
      case 'hed':
        return hasStudioHedExp;
      case 'any':
      default:
        return hasStudioExp || hasStudioHedExp;
      }
    });
  }

  /**
   * @method
   * @returns {boolean} - true if user has studio subscription
   */
  hasStudioSubscription(): Promise<boolean> {
    return this.getSubscriptionsV2().then(({ subscriptions }) => (
      subscriptions?.some(sub => STUDIO_CODES.includes(sub.code))
    ));
  }

  hasAcrobatLiteSubscription(): Promise<boolean> {
    return this.getSubscriptionsV2().then(({ subscriptions }) => (
      subscriptions?.some(sub => sub.family === 'ACROBAT_STUDIO_LITE')
    ));
  }

  /**
   * @method
   * @deprecated This method is deprecated and will be removed in a future version
   * @returns {boolean} - returns true if the user is a studio user, but has non free non studio subscription
   */
  isStudioUserWithOldSubscription(): Promise<boolean> {
    return Promise.all([
      this.isStudioUser(),
      this.getSubscriptionsV2(),
    ]).then(([isStudioUser, { subscriptions }]) => {
      // ignore non-DC subscriptions such as express
      const dcSubscriptions = subscriptions?.filter(sub => !NON_DC_SUBSCRIPTION_CODES.includes(sub.code));
      return isStudioUser
        && !dcSubscriptions?.some(sub => STUDIO_CODES.includes(sub.code))
        && dcSubscriptions?.some(sub => sub.code !== 'ACROBAT_FREE');
    });
  }

  async isReducedModeStudioUser(): Promise<boolean> {
    if (!auth2.isSignedIn) {
      return false;
    }
    const [isStudioUser, { subscriptions }] = await Promise.all([
      this.isStudioUser(),
      this.getSubscriptionsV2(),
    ]);
    // ignore non-DC subscriptions such as express
    const dcSubscriptions = subscriptions?.filter(sub => !NON_DC_SUBSCRIPTION_CODES.includes(sub.code));
    return isStudioUser && dcSubscriptions.length === 1 && dcSubscriptions[0].code === 'ACROBAT_FREE';
  }

  /**
   * @description
   * Checks if the current user has a student account.
   * A user is considered a student if:
   * - Their email ends with one of the allowed domains
   * - Their product arrangement codes include student-specific codes
   * - They are identified as an edu user via auth2 (if checkEduUser is true)
   * Options can be passed directly or configured via launchOptions.studentAccount
   * @method
   * @param {object} options - configuration options (overrides launchOptions.studentAccount)
   * @param {string[]} [options.studentProductCodes] - array of student product arrangement codes to check
   * @param {string[]} [options.allowedDomains] - array of allowed email domains (e.g., ['.edu', '.ac'])
   * @param {boolean} [options.checkEduUser] - whether to check auth2.isEduUser()
   * @returns {Promise<boolean>} - true if the user has a student account, false otherwise
   */
  async isStudentAccount(options: {
    studentProductCodes?: string[];
    allowedDomains?: string[];
    checkEduUser?: boolean;
  } = {}): Promise<boolean> {
    // Get defaults from launchOptions, then override with passed options
    const launchOptionsConfig = window.adobe_dc_sdk?.launchOptions?.studentAccount ?? {};
    const {
      studentProductCodes = launchOptionsConfig.studentProductCodes ?? STUDENT_PRODUCT_ARRANGEMENT_CODES,
      allowedDomains = launchOptionsConfig.allowedDomains ?? ['.edu', '.ac'],
      checkEduUser = launchOptionsConfig.checkEduUser ?? true,
    } = options;

    if (!auth2.isSignedIn) {
      return false;
    }

    const [productArrangementCodes, identity] = await Promise.all([
      this.getABPOfferProductCodes(),
      this.getIdentity(),
    ]);
    const { email, cohort } = identity;

    // Check if cohort includes StudySpace
    if (cohort?.includes('StudySpace')) {
      return true;
    }

    // Check if the email has one of the allowed domains
    const hasAllowedDomain = allowedDomains.some(domain => {
      // Escape special regex characters in the domain (e.g., dots)
      const escapedDomain = domain.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      // Match email ending with the domain (supports subdomains like user@sub.adobetest.com)
      const domainRegex = new RegExp(`^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]*${escapedDomain}$`, 'i');
      return domainRegex.test(email);
    });
    if (hasAllowedDomain) {
      return true;
    }

    // Check if user has student product arrangement codes
    const userProductCodes = productArrangementCodes?.split(',') || [];
    const hasStudentProductCode = userProductCodes.some(code => studentProductCodes.includes(code));
    if (hasStudentProductCode) {
      return true;
    }

    // Check if user is an edu user via auth2
    if (checkEduUser && auth2.isEduUser()) {
      return true;
    }

    return false;
  }
}

// This allows for providers.x().then() to be called before providers.x(config).
UserAPI.getInstance = getSingletonFunction(UserAPI);
export default UserAPI;
